const CONTROL_SIZES = ['xs', 'sm', 'md', 'lg', 'xlg'] as const;
const CONTROL_VARIANTS = ['outline', 'filled'] as const;
const ICON_VARIANTS = ['outline', 'solid', 'mini', 'micro'] as const;
const BUTTON_TYPES = ['button', 'submit', 'reset'] as const;
const ALIGNMENTS = ['start', 'center', 'end'] as const;
const POSITIONS = ['bottom', 'top', 'left', 'right'] as const;
const EDGE_POSITIONS = ['top', 'right', 'bottom', 'left'] as const;
const ORIENTATIONS = ['vertical', 'horizontal'] as const;
const INDICATOR_PLACEMENTS = ['leading', 'trailing', 'center', 'top-leading', 'top-trailing', 'bottom-leading', 'bottom-trailing'] as const;
const DEFAULT_VARIANTS = ['default'] as const;
const HEADING_LEVELS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
const TYPOGRAPHY_COLORS = ['inherit', 'muted', 'subtle', 'accent', 'danger'] as const;
const ICON_COLORS = TYPOGRAPHY_COLORS;
type TypographyColor = typeof TYPOGRAPHY_COLORS[number];
type IconColor = typeof ICON_COLORS[number];
const TYPOGRAPHY_COLOR_CLASSES = {
    inherit: 'text-inherit',
    muted: 'text-surface-ink-muted',
    subtle: 'text-surface-ink-muted',
    accent: 'text-intent-accent-text',
    danger: 'text-intent-danger-text',
} as const satisfies Record<TypographyColor, string>;
const ICON_COLOR_CLASSES = {
    inherit: 'text-current',
    muted: TYPOGRAPHY_COLOR_CLASSES.muted,
    subtle: TYPOGRAPHY_COLOR_CLASSES.subtle,
    accent: TYPOGRAPHY_COLOR_CLASSES.accent,
    danger: TYPOGRAPHY_COLOR_CLASSES.danger,
} as const satisfies Record<IconColor, string>;
const LAYOUT_SPACES = [
    '0', '0.5', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5',
    '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12',
] as const;
type LayoutSpace = typeof LAYOUT_SPACES[number];
const layoutSpaceClasses = (prefix: string): Record<LayoutSpace, string> => Object.fromEntries(
    LAYOUT_SPACES.map((space) => [space, space === '0' ? '' : `${prefix}-${space}`]),
) as Record<LayoutSpace, string>;
const LAYOUT_GAP_CLASSES = layoutSpaceClasses('gap');
const LAYOUT_GAP_X_CLASSES = layoutSpaceClasses('gap-x');
const LAYOUT_GAP_Y_CLASSES = layoutSpaceClasses('gap-y');
const LAYOUT_PADDING_CLASSES = layoutSpaceClasses('p');
const LAYOUT_PADDING_X_CLASSES = layoutSpaceClasses('px');
const LAYOUT_PADDING_Y_CLASSES = layoutSpaceClasses('py');
const LAYOUT_MARGIN_CLASSES = layoutSpaceClasses('m');
const LAYOUT_MARGIN_X_CLASSES = layoutSpaceClasses('mx');
const LAYOUT_MARGIN_Y_CLASSES = layoutSpaceClasses('my');
const COMPONENT_RADII = ['none', 'xs', 'sm', 'md', 'lg', 'full'] as const;
const ABSOLUTE_SURFACE_REFERENCES = ['0', '1', '2', '3', '4', '5', '6', '7', '8'] as const;
const SURFACE_REFERENCES = [
    'inherit', ...ABSOLUTE_SURFACE_REFERENCES,
    '+1', '+2', '+3', '+4', '+5', '+6', '+7', '+8',
] as const;
const ELEVATION_REFERENCES = ['none', 'match', '1', '2', '3', '4', '5', '6', '7', '8'] as const;
type SurfaceReference = typeof SURFACE_REFERENCES[number];
type AbsoluteSurfaceReference = typeof ABSOLUTE_SURFACE_REFERENCES[number];
type ElevationReference = typeof ELEVATION_REFERENCES[number];
const elevationReferenceClasses = (property: `--${string}`): Record<ElevationReference, string> => Object.fromEntries(
    ELEVATION_REFERENCES.map((reference) => {
        const value = reference === 'none'
            ? 'var(--shadow-elevation-0)'
            : reference === 'match'
                ? 'var(--ui-matched-elevation)'
                : `var(--shadow-elevation-${reference})`;
        return [reference, `[${property}:${value}]`];
    }),
) as Record<ElevationReference, string>;
const SURFACE_RADIUS_CLASSES = {
    none: '',
    xs: 'rounded-surface-xs',
    sm: 'rounded-surface-sm',
    md: 'rounded-surface-md',
    lg: 'rounded-surface-lg',
    full: 'rounded-full',
} as const satisfies Record<typeof COMPONENT_RADII[number], string>;
const CONTROL_RADIUS_CLASSES = {
    none: '',
    xs: 'rounded-control-xs',
    sm: 'rounded-control-sm',
    md: 'rounded-control-md',
    lg: 'rounded-control-lg',
    full: 'rounded-full',
} as const satisfies Record<typeof COMPONENT_RADII[number], string>;
const CONTROL_TYPOGRAPHY_CLASSES = {
    xs: 'font-control text-control-xs [font-weight:var(--font-weight-control-xs)]',
    sm: 'font-control text-control-sm [font-weight:var(--font-weight-control-sm)]',
    md: 'font-control text-control-md [font-weight:var(--font-weight-control-md)]',
    lg: 'font-control text-control-lg [font-weight:var(--font-weight-control-lg)]',
    xlg: 'font-control text-control-xlg [font-weight:var(--font-weight-control-xlg)]',
} as const;
const CONTROL_SIZE_CLASSES = {
    xs: `h-control-xs px-control-padding-xs ${CONTROL_TYPOGRAPHY_CLASSES.xs}`,
    sm: `h-control-sm px-control-padding-sm ${CONTROL_TYPOGRAPHY_CLASSES.sm}`,
    md: `h-control-md px-control-padding-md ${CONTROL_TYPOGRAPHY_CLASSES.md}`,
    lg: `h-control-lg px-control-padding-lg ${CONTROL_TYPOGRAPHY_CLASSES.lg}`,
    xlg: `h-control-xlg px-control-padding-xlg ${CONTROL_TYPOGRAPHY_CLASSES.xlg}`,
};
const CONTROL_MIN_SIZE_CLASSES = {
    xs: `min-h-control-xs px-control-padding-xs ${CONTROL_TYPOGRAPHY_CLASSES.xs}`,
    sm: `min-h-control-sm px-control-padding-sm ${CONTROL_TYPOGRAPHY_CLASSES.sm}`,
    md: `min-h-control-md px-control-padding-md ${CONTROL_TYPOGRAPHY_CLASSES.md}`,
    lg: `min-h-control-lg px-control-padding-lg ${CONTROL_TYPOGRAPHY_CLASSES.lg}`,
    xlg: `min-h-control-xlg px-control-padding-xlg ${CONTROL_TYPOGRAPHY_CLASSES.xlg}`,
};
const CONTROL_MULTILINE_SIZE_CLASSES = {
    xs: `min-h-control-multiline-xs px-control-padding-xs py-1 ${CONTROL_TYPOGRAPHY_CLASSES.xs}`,
    sm: `min-h-control-multiline-sm px-control-padding-sm py-1.5 ${CONTROL_TYPOGRAPHY_CLASSES.sm}`,
    md: `min-h-control-multiline-md px-control-padding-md py-2 ${CONTROL_TYPOGRAPHY_CLASSES.md}`,
    lg: `min-h-control-multiline-lg px-control-padding-lg py-2 ${CONTROL_TYPOGRAPHY_CLASSES.lg}`,
    xlg: `min-h-control-multiline-xlg px-control-padding-xlg py-2 ${CONTROL_TYPOGRAPHY_CLASSES.xlg}`,
};
const FIELD_CONTROL_ROOT_CLASSES = [
    'flex w-full min-w-0 items-center gap-control-gap outline-none',
];
const FIELD_CONTROL_MULTILINE_ROOT_CLASSES = [
    'flex w-full min-w-0 gap-control-gap outline-none',
];
const FIELD_CONTROL_INPUT_CLASSES = [
    'border-0 bg-transparent p-0 text-inherit outline-none',
];
const FIELD_CONTROL_ACTION_CLASSES = [
    'inline-flex size-control-xs shrink-0 items-center justify-center rounded-control-sm border-0 bg-transparent',
    'text-current outline-none',
];
const FIELD_CONTROL_NATIVE_CLASSES = [
    'w-full border-0 bg-transparent text-inherit outline-none',
];
const POPOVER_GEOMETRY_CLASSES = [
    'absolute left-0 top-0 z-popover m-0 grid h-auto',
    'outline-none',
];
const INTERACTIVE_ITEM_CLASSES = [
    'ui-interactive:hover:bg-interaction-hover',
    'ui-interactive:ui-active:bg-interaction-pressed',
    'ui-interactive:ui-selected:bg-interaction-selected',
    'ui-interactive:ui-selected:hover:bg-interaction-selected-hover',
];
const SELECTION_COLORS = ['neutral', 'accent'] as const;
const INTERACTION_TREATMENTS = ['tonal', 'edge', 'combined'] as const;
const OPTION_ITEM_CLASSES = [
    'flex h-menu-item w-full cursor-default select-none items-center gap-2 rounded-control-sm px-menu-item-padding-inline',
    'text-left font-menu-item text-menu-item [font-weight:var(--font-weight-menu-item)] outline-none',
    'data-disabled:pointer-events-none',
];
const OPTION_ICON_CLASSES = 'size-icon shrink-0 fill-current text-current opacity-70';
const OPTION_INDICATOR_CLASSES = 'ml-auto size-icon shrink-0 fill-current text-current opacity-0 data-selected:opacity-100';

type ControlSize = typeof CONTROL_SIZES[number];
type ControlVariant = typeof CONTROL_VARIANTS[number];
type IconVariant = typeof ICON_VARIANTS[number];
type CommonButtonType = typeof BUTTON_TYPES[number];
type Alignment = typeof ALIGNMENTS[number];
type Position = typeof POSITIONS[number];
type EdgePosition = typeof EDGE_POSITIONS[number];
type Orientation = typeof ORIENTATIONS[number];
type IndicatorPlacement = typeof INDICATOR_PLACEMENTS[number];
type SelectionColor = typeof SELECTION_COLORS[number];
type InteractionTreatment = typeof INTERACTION_TREATMENTS[number];
type DefaultVariant = typeof DEFAULT_VARIANTS[number];
type HeadingLevel = typeof HEADING_LEVELS[number];
type ComponentRadius = typeof COMPONENT_RADII[number];

export {
    ABSOLUTE_SURFACE_REFERENCES,
    ALIGNMENTS,
    BUTTON_TYPES,
    COMPONENT_RADII,
    CONTROL_RADIUS_CLASSES,
    CONTROL_MIN_SIZE_CLASSES,
    CONTROL_MULTILINE_SIZE_CLASSES,
    CONTROL_SIZES,
    CONTROL_SIZE_CLASSES,
    CONTROL_TYPOGRAPHY_CLASSES,
    CONTROL_VARIANTS,
    DEFAULT_VARIANTS,
    EDGE_POSITIONS,
    ELEVATION_REFERENCES,
    FIELD_CONTROL_ACTION_CLASSES,
    FIELD_CONTROL_INPUT_CLASSES,
    FIELD_CONTROL_MULTILINE_ROOT_CLASSES,
    FIELD_CONTROL_NATIVE_CLASSES,
    FIELD_CONTROL_ROOT_CLASSES,
    elevationReferenceClasses,
    HEADING_LEVELS,
    ICON_COLORS,
    ICON_COLOR_CLASSES,
    ICON_VARIANTS,
    INDICATOR_PLACEMENTS,
    INTERACTION_TREATMENTS,
    INTERACTIVE_ITEM_CLASSES,
    LAYOUT_GAP_CLASSES,
    LAYOUT_GAP_X_CLASSES,
    LAYOUT_GAP_Y_CLASSES,
    LAYOUT_MARGIN_CLASSES,
    LAYOUT_MARGIN_X_CLASSES,
    LAYOUT_MARGIN_Y_CLASSES,
    LAYOUT_PADDING_CLASSES,
    LAYOUT_PADDING_X_CLASSES,
    LAYOUT_PADDING_Y_CLASSES,
    LAYOUT_SPACES,
    OPTION_ICON_CLASSES,
    OPTION_INDICATOR_CLASSES,
    OPTION_ITEM_CLASSES,
    ORIENTATIONS,
    POPOVER_GEOMETRY_CLASSES,
    POSITIONS,
    SELECTION_COLORS,
    SURFACE_RADIUS_CLASSES,
    SURFACE_REFERENCES,
    TYPOGRAPHY_COLOR_CLASSES,
    TYPOGRAPHY_COLORS,
};
export type {InteractionTreatment, SelectionColor};
export type {
    AbsoluteSurfaceReference,
    Alignment,
    CommonButtonType,
    ComponentRadius,
    ControlSize,
    ControlVariant,
    DefaultVariant,
    EdgePosition,
    ElevationReference,
    HeadingLevel,
    IconColor,
    IconVariant,
    IndicatorPlacement,
    LayoutSpace,
    Orientation,
    Position,
    SurfaceReference,
    TypographyColor,
};
